import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { AnchorbaseLogo } from '@/components/branding/AnchorbaseLogo';
import {
    SAVE_CARD_FOR_FASTER_CHECKOUT_LABEL,
    SAVE_CARD_FOR_FASTER_CHECKOUT_TOOLTIP,
} from '@/lib/save-card-copy';

type CustomerSelfOnboardingFormState = {
    email: string;
    firstName: string;
    lastName: string;
    password: string;
    confirmPassword: string;
};

function resolveErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message.trim().length > 0) {
        return error.message;
    }
    return 'Unable to create your customer portal account.';
}

export default function CustomerSelfOnboardingPage() {
    const log = createComponentLogger('CustomerSelfOnboardingPage');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const prefilledEmail = useMemo(() => searchParams.get('email')?.trim() ?? '', [searchParams]);
    const paymentRequestUuid = useMemo(
        () => searchParams.get('payment_request_uuid')?.trim() ?? '',
        [searchParams]
    );
    const [formState, setFormState] = useState<CustomerSelfOnboardingFormState>({
        email: prefilledEmail,
        firstName: '',
        lastName: '',
        password: '',
        confirmPassword: '',
    });
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [storePaymentMethodForFutureUse, setStorePaymentMethodForFutureUse] =
        useState<boolean>(false);

    const onboardingMutation = useMutation({
        mutationFn: async () =>
            await authApi.createCustomerSelfOnboarding({
                email: formState.email.trim(),
                first_name: formState.firstName.trim(),
                last_name: formState.lastName.trim(),
                password: formState.password,
                ...(paymentRequestUuid.length > 0
                    ? { payment_request_uuid: paymentRequestUuid }
                    : {}),
                ...(paymentRequestUuid.length > 0 && storePaymentMethodForFutureUse
                    ? { store_payment_method_for_future_use: true }
                    : {}),
            }),
        onSuccess: (response) => {
            runInBackground(
                navigate(`/login?email=${encodeURIComponent(response.primary_email)}`),
                (error) => {
                    log.error('Error navigating to login after self-onboarding', error);
                }
            );
        },
        onError: (error: unknown) => {
            setErrorMessage(resolveErrorMessage(error));
        },
    });

    const passwordsMatch =
        formState.password.length > 0 && formState.password === formState.confirmPassword;
    const formIsValid =
        formState.email.trim().length > 0 &&
        formState.firstName.trim().length > 0 &&
        formState.lastName.trim().length > 0 &&
        formState.password.length >= 8 &&
        passwordsMatch;

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setErrorMessage('');
        if (!formIsValid) {
            return;
        }
        onboardingMutation.mutate();
    };

    return (
        <div className="flex min-h-screen items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-purple-50">
            <Card className="w-full max-w-md">
                <CardHeader className="space-y-1">
                    <CardTitle className="text-3xl font-bold text-center flex justify-center">
                        <AnchorbaseLogo width={280} />
                    </CardTitle>
                    <CardDescription className="text-center">
                        Create your customer portal account
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="you@example.com"
                                value={formState.email}
                                onChange={(event) =>
                                    setFormState((current) => ({
                                        ...current,
                                        email: event.target.value,
                                    }))
                                }
                                readOnly={prefilledEmail.length > 0}
                                disabled={onboardingMutation.isPending}
                            />
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="first-name">First Name</Label>
                                <Input
                                    id="first-name"
                                    type="text"
                                    placeholder="First name"
                                    value={formState.firstName}
                                    onChange={(event) =>
                                        setFormState((current) => ({
                                            ...current,
                                            firstName: event.target.value,
                                        }))
                                    }
                                    disabled={onboardingMutation.isPending}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="last-name">Last Name</Label>
                                <Input
                                    id="last-name"
                                    type="text"
                                    placeholder="Last name"
                                    value={formState.lastName}
                                    onChange={(event) =>
                                        setFormState((current) => ({
                                            ...current,
                                            lastName: event.target.value,
                                        }))
                                    }
                                    disabled={onboardingMutation.isPending}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                placeholder="Create a password"
                                value={formState.password}
                                onChange={(event) =>
                                    setFormState((current) => ({
                                        ...current,
                                        password: event.target.value,
                                    }))
                                }
                                disabled={onboardingMutation.isPending}
                            />
                            <p className="text-xs text-muted-foreground">
                                Password must be at least 8 characters long.
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirm-password">Confirm Password</Label>
                            <Input
                                id="confirm-password"
                                type="password"
                                placeholder="Confirm your password"
                                value={formState.confirmPassword}
                                onChange={(event) =>
                                    setFormState((current) => ({
                                        ...current,
                                        confirmPassword: event.target.value,
                                    }))
                                }
                                disabled={onboardingMutation.isPending}
                            />
                        </div>

                        {paymentRequestUuid.length > 0 ? (
                            <label
                                className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700"
                                title={SAVE_CARD_FOR_FASTER_CHECKOUT_TOOLTIP}
                            >
                                <input
                                    type="checkbox"
                                    checked={storePaymentMethodForFutureUse}
                                    onChange={(event) =>
                                        setStorePaymentMethodForFutureUse(
                                            event.target.checked
                                        )
                                    }
                                    className="mt-0.5 h-4 w-4"
                                    disabled={onboardingMutation.isPending}
                                />
                                <span>{SAVE_CARD_FOR_FASTER_CHECKOUT_LABEL}</span>
                            </label>
                        ) : null}

                        {formState.confirmPassword.length > 0 && !passwordsMatch ? (
                            <Alert variant="destructive">
                                <AlertDescription>Passwords do not match.</AlertDescription>
                            </Alert>
                        ) : null}

                        {errorMessage.length > 0 ? (
                            <Alert variant="destructive">
                                <AlertDescription>{errorMessage}</AlertDescription>
                            </Alert>
                        ) : null}

                        <Button
                            type="submit"
                            className="w-full"
                            disabled={onboardingMutation.isPending || !formIsValid}
                        >
                            {onboardingMutation.isPending
                                ? 'Creating Account...'
                                : 'Create Account'}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
